import { TrainingManagerPermissionsDialog } from '@/components/TrainingManagerPermissionsDialog';
import { Location } from '@/interfaces/ProviderTraining';
import { BaseSelectorOption, RenderSelectorProps } from '@/types';
import {
  Box,
  Divider,
  Fade,
  Link,
  ListItemText,
  Menu,
  MenuItem,
  SelectChangeEvent,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Tab,
  Tabs,
} from '@mui/material';
import { Alert, EditTemplate, Utils } from 'klayowebshared';
import { cloneDeep, debounce } from 'lodash';
import React, { Component, SyntheticEvent } from 'react';
import { AppContext } from '../../common/AppContext';
import { ErrorModel } from '../../common/models/ErrorModel';
import { Button } from '../../components/Button';
import { PaperOrDialog } from '../../components/PaperOrDialog';
import { PillButtons } from '../../components/PillButtons';
import { Placeholder } from '../../components/Placeholder';
import { TextField } from '../../components/TextField';
import { TooltipIcon } from '../../components/TooltipIcon';
import { EmployeeSelector } from '../../components/selectors/EmployeeSelector';
import { JobSelector } from '../../components/selectors/JobSelector';
import { LocationSelector } from '../../components/selectors/LocationSelector';
import { MultiSelector } from '../../components/selectors/MultiSelector';
import { EmployeeTable } from '../../components/table/EmployeeTable';
import { PAGE_SIZE_FOR_ADD_EDIT } from '../../constants';
import { Data } from '../../data/Data';
import { SettingsEmployee } from '../../data/settings/SettingsEmployee';
import { MemberSelectionTypeOption, SettingsGroup } from '../../data/settings/SettingsGroup';
import { SettingsGroupMemberCount } from '../../data/settings/SettingsGroupMemberCount';
import { SettingsGroupMemberList } from '../../data/settings/SettingsGroupMemberList';
import { SettingsJob } from '../../data/settings/SettingsJob';
import { SettingsJobList } from '../../data/settings/SettingsJobList';
import { withLocale } from '../../providers/LocaleProvider';
import LeftIcon from '../../resources/images/icons-arrow-left.svg';
import RightIcon from '../../resources/images/icons-arrow-right.svg';
import UserIcon from '../../resources/images/icons-users-1.svg';
import type {
  AddEditGroupViewProps,
  AddEditGroupViewState,
  GroupApiResponse,
  GroupLevelTypeChangeEvent,
} from '../../types/views/group.types';
import { GroupMembersDialog } from './GroupMembersDialog';

class AddEditGroupViewBase extends Component<AddEditGroupViewProps, AddEditGroupViewState> {
  static contextType = AppContext;
  declare context: React.ContextType<typeof AppContext>;

  static defaultProps = {
    allowEmptyMembers: true,
    allowEmptyManagers: true,
  };

  existingGroup: SettingsGroup;
  onSearchJobs: ReturnType<typeof debounce>;

  constructor(props: AddEditGroupViewProps) {
    super(props);

    this.existingGroup = new SettingsGroup(props.group);
    this.onSearchJobs = debounce(this.onSearchJobsInternal.bind(this), 300);

    this.state = {
      group: props.group,
      groupLoaded: !props.editMode,
      actionEmployee: null,
      actionTarget: null,
      jobs: null,
      jobsSearchList: null,
      jobsSearch: null,
      jobSelected: null,
      error: props.error ?? null,
      step: 0,
      hasEdit: false,
      saving: false,
      manualManagerMemberOverlap: this.getManagerMemberOverlap(this.existingGroup),
      ruleBasedMembersCount: null,
      updatingRuleBasedMembersCount: false,
      ruleBasedMembers: null,
      showRuleBasedEmployeesDialog: false,
      isOpenTrainingManagerPermissionsDialog: false,
    };
  }

  componentDidMount() {
    const { onLoadJobs, group } = this.props;
    this.loadDetails();
    this.loadJobs();
    onLoadJobs?.();
  }

  componentDidUpdate(prevProps: AddEditGroupViewProps, prevState: AddEditGroupViewState): void {
    if (this.props.error !== prevProps.error) this.setState({ error: this.props.error ?? null });
    if (prevProps.jobs !== this.props.jobs) this.forceUpdate();
  }

  loadDetails(): void {
    const { group } = this.state;
    const groupId = group ? group.groupId : this.props.groupId;

    if (!groupId) return;

    this.context.setLoading?.('loading', true);

    Data.get<GroupApiResponse>(this.context, '/Group/' + groupId)
      .then((response) => {
        const group = new SettingsGroup(response.data.group);
        this.existingGroup = new SettingsGroup(response.data.group);

        this.setState(
          {
            group,
            jobSelected: group.jobDefinitionIds,
            groupLoaded: true,
            saving: false,
            manualManagerMemberOverlap: this.getManagerMemberOverlap(group),
          },
          () => {
            if (group && group.isRuleBasedSelection()) this.getRuleBasedEmployeeCount();
          }
        );
      })
      .catch((e: unknown) => {
        this.setState({ error: ErrorModel.parseServerError(e) });
      })
      .finally(() => {
        this.context.setLoading?.('loading', false);
      });
  }

  loadJobs(searchText?: string): void {
    this.context.setLoading?.('jobs', true);

    const pagingOptions = searchText
      ? { pageNumber: 1, pageSize: PAGE_SIZE_FOR_ADD_EDIT, searchText: searchText }
      : { pageSize: 100000 };

    // get setting job data then set to jobs state
    if (searchText) {
      SettingsJobList.getWithPaging(this.context, pagingOptions)
        .then((jobs) => {
          this.setState({ jobsSearchList: jobs as SettingsJobList, jobsSearch: searchText });
        })
        .catch(() => {})
        .finally(() => this.context.setLoading?.('jobs', false));
    } else {
      SettingsJobList.getWithPaging(this.context, pagingOptions)
        .then((jobs) => {
          const jobsList = jobs as SettingsJobList;
          const jobsSearchList = {
            ...jobsList,
            jobs: jobsList.jobs.slice(0, PAGE_SIZE_FOR_ADD_EDIT - 1),
          } as SettingsJobList;

          // job state need to be separated from search job state because it was used to take selected job in multiselect
          this.setState({ jobs: jobsList, jobsSearchList, jobsSearch: searchText ?? null });
        })
        .catch(() => {})
        .finally(() => this.context.setLoading?.('jobs', false));
    }
  }

  onSearchJobsInternal(searchText: string): void {
    this.loadJobs(searchText);
  }

  onJobsBlur(): void {
    const { jobsSearch } = this.state;

    if (jobsSearch) this.loadJobs(jobsSearch);
  }

  onBack(): void {
    this.setState({ step: 0 });
  }

  onNavigation(e: SyntheticEvent, callback: (e: SyntheticEvent) => void): void {
    const { onNavigation } = this.props;
    if (onNavigation) onNavigation(e, callback);
  }

  onCancel(e: SyntheticEvent, source?: string): void {
    this.onNavigation(e, this.stopBlockingNavAndClose.bind(this));
  }

  stopBlockingNavAndClose(e: SyntheticEvent): void {
    const { onCancel, onBlockNavigation } = this.props;
    if (onBlockNavigation) onBlockNavigation(false, 'Group');
    if (onCancel) onCancel(e);
  }

  onNameChange(
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>
  ): void {
    const { group } = this.state;
    if (group) group.name = e.target.value;
    this.setState({ group });
  }

  onDescriptionChange(
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>
  ): void {
    const { group } = this.state;
    if (group) group.description = e.target.value;
    this.setState({ group });
  }

  isNameValid(group?: SettingsGroup | null): boolean {
    return (group && group.name && group.name.length > 2) === true;
  }

  isValid(group?: SettingsGroup | null, step?: number): boolean {
    const { allowEmptyMembers, allowEmptyManagers } = this.props;

    if (!group || !this.isNameValid(group)) return false;
    if (!allowEmptyMembers && group.isManualSelection() && group.members.length < 1) return false;
    if (group.isRuleBasedSelection() && !this.isRuleValid(group)) return false;

    if (step === 1) {
      if (!allowEmptyManagers && group.managers.length < 1) return false;
      if (
        group.managers.some((m: SettingsEmployee) => !m.groupLevelType || !m.groupNotificationType)
      )
        return false;
    }
    return true;
  }

  isRuleValid(group: SettingsGroup): boolean {
    return (
      (group.jobDefinitionIds.length > 0 || group.includeAllJobDefinitions === true) &&
      (group.locationIds.length > 0 || group.includeAllLocations === true)
    );
  }

  onTabChange(e: SyntheticEvent, newStep: number): void {
    this.setState({ step: newStep, error: null });
  }

  onSave(): void {
    const { onSave, onBlockNavigation, editMode } = this.props;
    const { group, step } = this.state;

    if (!editMode && step === 0 && this.isValid(group, 0)) this.setState({ step: 1, error: null });
    if (onSave && (editMode || step === 1)) {
      if (onBlockNavigation) onBlockNavigation(false, 'Group');
      this.setState({ saving: true });
      onSave(group, () => {
        this.setState({ saving: false });
      });
    }
  }

  onMemberSelectionTypeChange(
    e: SyntheticEvent,
    memberSelectionType: MemberSelectionTypeOption
  ): void {
    const { group } = this.state;
    if (!group) return;
    group.memberSelectionType = memberSelectionType;
    this.setState({ group, manualManagerMemberOverlap: this.getManagerMemberOverlap(group) });
  }

  onJobsChange(e: SyntheticEvent | null, jobs: SettingsJob[], isAll?: boolean): void {
    const { group, jobsSearchList } = this.state;

    if (!group) return;

    //save only Job ID
    group.includeAllJobDefinitions = isAll === true;
    group.jobDefinitionIds = isAll
      ? []
      : jobs
          .map((j: SettingsJob) => j.jobDefinitionId!)
          .filter((id): id is string => id !== undefined);

    this.setState({ group, jobSelected: group.jobDefinitionIds }, () => this.onRuleChange(e));
  }

  onLocationsChange(e: SyntheticEvent | null, locations: Location[], isAll?: boolean): void {
    const { group } = this.state;
    if (!group) return;

    group.includeAllLocations = isAll === true;
    group.locationIds = isAll ? [] : locations.map((loc) => loc.locationId);
    this.setState({ group }, () => this.onRuleChange(e));
  }

  onRuleChange(e: SyntheticEvent | null): void {
    const { group } = this.state;
    this.getRuleBasedEmployeeCount();
  }

  getRuleBasedEmployeeCount(): void {
    const { group } = this.state;
    if (!group) return;
    this.context.setLoading?.('calculateMembers', true);

    this.setState({ updatingRuleBasedMembersCount: true });

    group
      .loadRuleBasedMembersCount(this.context as any, true)
      .then((ruleBasedMembersCount: SettingsGroupMemberCount) => {
        this.setState({ ruleBasedMembersCount, updatingRuleBasedMembersCount: false });
      })
      .catch((e: unknown) => {
        this.setState({
          error: ErrorModel.parseServerError(e),
          ruleBasedMembersCount: null,
          updatingRuleBasedMembersCount: false,
        });
      })
      .finally(() => this.context.setLoading?.('calculateMembers', false));
  }

  onEditStateChange(hasEdit: boolean): void {
    this.setState({ hasEdit });
  }

  onMembersChange(e: SyntheticEvent, employee: SettingsEmployee | null): void {
    if (!employee) return;
    const { group } = this.state;
    if (!group) return;

    group.addMember(employee);
    this.setState({ group, manualManagerMemberOverlap: this.getManagerMemberOverlap(group) });
  }

  onRemoveMember(e: SyntheticEvent, employee: SettingsEmployee | null): void {
    if (!employee) return;
    const { group } = this.state;
    if (!group) return;

    group.removeMember(employee);
    this.setState({ group, manualManagerMemberOverlap: this.getManagerMemberOverlap(group) });
  }

  onManagerChange(e: SyntheticEvent, employee: SettingsEmployee | null): void {
    if (!employee) return;
    const { group } = this.state;
    if (!group) return;

    group.addManager(employee);
    this.setState({ group, manualManagerMemberOverlap: this.getManagerMemberOverlap(group) }, () =>
      this.onRuleChange(e)
    );
  }

  onRemoveManager(e: SyntheticEvent, employee: SettingsEmployee | null): void {
    if (!employee) return;
    const { group } = this.state;
    if (!group) return;

    this.onCloseActionMenu();

    group.removeManager(employee);
    this.setState({ group, manualManagerMemberOverlap: this.getManagerMemberOverlap(group) }, () =>
      this.onRuleChange(e)
    );
  }

  onManagerGroupLevelTypeChange(
    e: GroupLevelTypeChangeEvent,
    manager: SettingsEmployee | null
  ): void {
    if (!manager) return;
    manager.groupLevelType = e.target.value.id;
    this.forceUpdate();
  }

  onManagerGroupNotificationTypesChange(
    e: GroupLevelTypeChangeEvent,
    manager: SettingsEmployee | null
  ): void {
    if (!manager) return;
    manager.groupNotificationType = e.target.value.id;
    this.forceUpdate();
  }

  getManagerMemberOverlap(group: SettingsGroup | null): SettingsEmployee[] {
    if (!group) return [];
    return group.managers.filter((m: SettingsEmployee) =>
      group.members.some((e: SettingsEmployee) => m.employeeId === e.employeeId)
    );
  }

  getTranslatedGroupLevelTypes():
    | Array<{ id: number | string; label: string; name: string }>
    | undefined {
    const { orgSettings } = this.props;
    if (!orgSettings?.groupLevelTypes) return undefined;

    const labelMap: Record<string, string> = {
      Off: this.props.translate('addEditGroup.permissionOff'),
      View: this.props.translate('addEditGroup.permissionView'),
      Manage: this.props.translate('addEditGroup.permissionManage'),
    };

    return orgSettings.groupLevelTypes.map((type: { id: number | string; label: string }) => ({
      ...type,
      label: labelMap[type.label] || type.label,
    }));
  }

  getTranslatedGroupNotificationTypes():
    | Array<{ id: number | string; label: string; name: string }>
    | undefined {
    const { orgSettings } = this.props;
    if (!orgSettings?.groupNotificationTypes) return undefined;

    const labelMap: Record<string, string> = {
      Off: this.props.translate('addEditGroup.notificationOff'),
      Expiry: this.props.translate('addEditGroup.notificationExpiry'),
      All: this.props.translate('addEditGroup.notificationAll'),
    };

    return orgSettings.groupNotificationTypes.map(
      (type: { id: number | string; label: string }) => ({
        ...type,
        label: labelMap[type.label] || type.label,
      })
    );
  }

  renderManagerMemberOverlapAlert(): React.ReactNode {
    const { group, manualManagerMemberOverlap } = this.state;

    const overlap =
      group &&
      group.isRuleBasedSelection() &&
      group.ruleBasedMembersCount instanceof SettingsGroupMemberCount
        ? group.ruleBasedMembersCount.managerMemberOverlap
        : manualManagerMemberOverlap;

    if (!overlap || overlap.length === 0) return null;

    const overlapMessage =
      overlap.length === 1
        ? this.props.translate('addEditGroup.managerMemberOverlapSingular')
        : this.props.translate('addEditGroup.managerMemberOverlapPlural');

    return (
      <Alert severity='warning'>
        <b>{overlap.map((m: SettingsEmployee) => m.fullName).join(', ')}</b> {overlapMessage}
      </Alert>
    );
  }

  onViewRuleBasedEmployees(e: SyntheticEvent): void {
    const { group } = this.state;
    if (!group) return;

    this.context.setLoading?.('viewMembers', true);

    group
      .loadRuleBasedMembers(this.context as any, true)
      .then((ruleBasedMembers: SettingsGroupMemberList) => {
        this.setState({ ruleBasedMembers });
      })
      .catch((e: unknown) => {
        this.setState({ error: ErrorModel.parseServerError(e), ruleBasedMembers: null });
      })
      .finally(() => this.context.setLoading?.('viewMembers', false));

    this.setState({ showRuleBasedEmployeesDialog: true, ruleBasedMembers: null });
  }

  onCloseRuleBasedEmployeesDialog(e: SyntheticEvent): void {
    this.setState({ showRuleBasedEmployeesDialog: false });
  }

  onAccessEmployeeAction(e: React.MouseEvent<HTMLElement>, employee: SettingsEmployee): void {
    this.setState({ actionTarget: e.currentTarget, actionEmployee: employee });
  }

  onCloseActionMenu(): void {
    this.setState({ actionTarget: null, actionEmployee: null });
  }

  onAssignAsInstructor(isInstructor: boolean): void {
    const { actionEmployee, group } = this.state;
    if (!group) return;

    const managers = cloneDeep(group.managers);
    this.onCloseActionMenu();

    if (group && actionEmployee) {
      const editedManagerIndex = managers.findIndex(
        (e: SettingsEmployee) => e.employeeId === actionEmployee.employeeId
      );

      if (editedManagerIndex !== -1) {
        managers[editedManagerIndex].isInstructor = isInstructor;
        group.managers = [...managers];
      }
    }
  }

  onOpenTrainingManagerPermissionsDialog() {
    this.setState({
      isOpenTrainingManagerPermissionsDialog: true,
      actionTarget: null,
    });
  }

  onCloseTrainingManagerPermissionsDialog() {
    this.setState({
      isOpenTrainingManagerPermissionsDialog: false,
      actionEmployee: null,
    });
  }

  async onAssignTrainingManagerPermissions(
    trainingManagerConfigurationTypeId?: string,
    trainingManagerConfigurationTypeName?: string
  ) {
    const { actionEmployee, group } = this.state;
    if (!group) return;

    const managers = cloneDeep(group.managers);

    if (group && actionEmployee) {
      const editedManagerIndex = managers.findIndex(
        (e: SettingsEmployee) => e.employeeId === actionEmployee.employeeId
      );

      if (editedManagerIndex !== -1) {
        managers[editedManagerIndex].isTrainingManager = !!trainingManagerConfigurationTypeId;
        managers[editedManagerIndex].trainingManagerConfigurationTypeId =
          trainingManagerConfigurationTypeId || undefined;
        managers[editedManagerIndex].trainingManagerConfigurationTypeName =
          trainingManagerConfigurationTypeName || undefined;
        group.managers = [...managers];
      }
    }

    this.onCloseTrainingManagerPermissionsDialog();
  }

  render() {
    const {
      organization,
      theme,
      user,
      orgSettings,
      editMode,
      locations,
      onAllowNavigation,
      onBlockNavigation,
      employees,
      groupMembers,
    } = this.props;
    const {
      error,
      group,
      actionEmployee,
      actionTarget,
      groupLoaded,
      jobs,
      jobSelected,
      jobsSearchList,
      step,
      ruleBasedMembers,
      ruleBasedMembersCount,
      updatingRuleBasedMembersCount,
      showRuleBasedEmployeesDialog,
      saving,
      search,
      hasEdit,
    } = this.state;

    return (
      <>
        <EditTemplate
          theme={theme}
          name='Group'
          onAllowNavigation={onAllowNavigation}
          onBlockNavigation={onBlockNavigation}
          onEditStateChange={this.onEditStateChange.bind(this)}
          detectEdit={!saving}
          unsavedChangesTitle={this.props.translate('common.unsavedChangesTitle')}
          unsavedChangesQuestion={this.props.translate('common.unsavedChangesQuestion')}
          unsavedChangesAcceptButton={this.props.translate('common.leavePage')}
          unsavedChangesCancelButton={this.props.translate('common.cancel')}
          compare={{
            existing: this.existingGroup,
            editing: group,
            members: [
              { name: 'name' },
              { name: 'description' },
              {
                name: 'members',
                detectEdit: (existing: SettingsGroup, editing: SettingsGroup) =>
                  !existing.compareMemberSelection(editing),
              },
              {
                name: 'managers',
                detectEdit: (existing: SettingsGroup, editing: SettingsGroup) =>
                  !existing.compareManagers(editing),
              },
            ],
          }}
        >
          <PaperOrDialog
            theme={theme}
            dialogToBreakpoint='md'
            disabledToBreakpoint='md'
            paperPadding='60px'
            title={
              editMode
                ? this.props.translate('addEditGroup.groupDetails')
                : this.props.translate('addEditGroup.createGroup')
            }
            titleSx={{ fontSize: { xs: '20px', md: '30px' }, fontWeight: 'bold' }}
            titleSize={1}
            backButton={editMode}
            onBack={this.onCancel.bind(this)}
          >
            <Stack direction='column' spacing={4}>
              {error && (
                <Alert severity='error' sx={{ width: '100%' }}>
                  {error}
                </Alert>
              )}

              {!editMode ? (
                <Stepper activeStep={step}>
                  <Step
                    onClick={this.onBack.bind(this)}
                    sx={{ cursor: step !== 0 ? 'pointer' : 'default' }}
                  >
                    <StepLabel>{this.props.translate('addEditGroup.groupInformation')}</StepLabel>
                  </Step>
                  <Step>
                    <StepLabel>{this.props.translate('addEditGroup.groupAccess')}</StepLabel>
                  </Step>
                </Stepper>
              ) : (
                <div>
                  <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs
                      centered={true}
                      value={step}
                      onChange={this.onTabChange.bind(this)}
                      variant='fullWidth'
                      sx={{ width: '100%' }}
                    >
                      <Tab label={this.props.translate('addEditGroup.information')} value={0} />
                      <Tab label={this.props.translate('addEditGroup.access')} value={1} />
                    </Tabs>
                  </Box>
                </div>
              )}

              {step === 0 && (
                <Fade in={true}>
                  <div>
                    <Stack direction='column' spacing={4}>
                      <div>
                        <h2>{this.props.translate('addEditGroup.groupDetails')}</h2>
                        <Stack direction='column' spacing={4} sx={{ padding: '16px 0' }}>
                          <TextField
                            label={this.props.translate('addEditGroup.groupNameRequired')}
                            value={group ? group.name : null}
                            clearable={false}
                            autoComplete={false}
                            onChange={this.onNameChange.bind(this)}
                            validationMethod={() => this.isNameValid(group)}
                            helperText={this.props.translate('addEditGroup.nameMustBeUnique')}
                            validationText={this.props.translate(
                              'addEditGroup.validGroupNameRequired'
                            )}
                            loading={!groupLoaded}
                            disabled={saving}
                            sx={{ width: '100%' }}
                          />

                          <TextField
                            label={this.props.translate('addEditGroup.description')}
                            autoComplete={false}
                            multiline={true}
                            clearable={false}
                            minRows={3}
                            sx={{ width: '100%' }}
                            value={group ? group.description : null}
                            disabled={saving}
                            loading={!groupLoaded}
                            onChange={this.onDescriptionChange.bind(this)}
                          />
                        </Stack>
                      </div>
                      <div>
                        <Divider />
                      </div>
                      <div>
                        <h2>{this.props.translate('addEditGroup.groupMembers')}</h2>
                        <Stack direction='column' spacing={4} sx={{ padding: '16px 0' }}>
                          {!editMode && (
                            <Stack spacing={2} alignItems='end' direction='row'>
                              <PillButtons
                                label={this.props.translate('addEditGroup.memberSelection')}
                                user={user}
                                value={
                                  group
                                    ? group.memberSelectionType
                                    : SettingsGroup.memberSelectionType.manual
                                }
                                organization={organization}
                                onChange={this.onMemberSelectionTypeChange.bind(this)}
                                pages={{
                                  standard: {
                                    name: this.props.translate(
                                      SettingsGroup.memberSelectionType.manual.translationKey
                                    ),
                                    value: SettingsGroup.memberSelectionType.manual,
                                    tooltip: null,
                                  },
                                  parent: {
                                    name: this.props.translate(
                                      SettingsGroup.memberSelectionType.ruleBased.translationKey
                                    ),
                                    value: SettingsGroup.memberSelectionType.ruleBased,
                                    tooltip: null,
                                  },
                                }}
                                disabled={saving}
                                sx={{ alignSelf: 'flex-start' }}
                              />
                              <TooltipIcon
                                text={this.props.translate('addEditGroup.memberSelectionTooltip')}
                                placement='right'
                                sx={{ paddingBottom: '2px' }}
                              />
                            </Stack>
                          )}

                          {(!group || group.isManualSelection()) && (
                            <Fade in={true}>
                              <div>
                                <Stack spacing={2} direction='column'>
                                  <EmployeeSelector
                                    theme={theme}
                                    hasNoneItem={false}
                                    multiple={false}
                                    label={this.props.translate('addEditGroup.members')}
                                    clearable={false}
                                    clearOnSelection={true}
                                    clearResultsOnSelection={true}
                                    excludeItems={group ? (group.members as any) : null}
                                    placeholder={this.props.translate(
                                      'addEditGroup.searchAndAddEmployees'
                                    )}
                                    disabled={saving}
                                    onChange={this.onMembersChange.bind(this) as any}
                                    initialItems={groupMembers ? (groupMembers as any) : null}
                                    callApi={false}
                                    translate={this.props.translate}
                                  />

                                  {this.renderManagerMemberOverlapAlert()}

                                  {group?.members?.some(
                                    (member: SettingsEmployee) => member.fromDirectory
                                  ) && (
                                    <Alert severity='info' sx={{ mb: 1 }}>
                                      {this.props.translate(
                                        group.canOverrideDirectory
                                          ? 'employees.groupMemberFromDirectory'
                                          : 'employees.groupMembersSetInDirectory'
                                      )}
                                    </Alert>
                                  )}

                                  {group && group.members && group.members.length > 0 && (
                                    <EmployeeTable
                                      pagination={false}
                                      showFooter={false}
                                      theme={theme}
                                      showHeaderFromBreakpoint='md'
                                      translate={this.props.translate}
                                      dense={true}
                                      columns={[
                                        { type: EmployeeTable.columns.fullName },
                                        { type: EmployeeTable.columns.jobTitle },
                                        {
                                          type: EmployeeTable.columns.removeAction,
                                          tooltip: this.props.translate(
                                            'addEditGroup.removeMember'
                                          ),
                                          label: '',
                                        } as any,
                                      ]}
                                      sortModel={[
                                        {
                                          field: 'fullName',
                                          sort: 'asc',
                                        },
                                      ]}
                                      onRemove={this.onRemoveMember.bind(this) as any}
                                      rowHasAction={
                                        group.canOverrideDirectory
                                          ? undefined
                                          : (member) => !member.fromDirectory
                                      }
                                      paper={false}
                                      disabled={saving}
                                      rows={group.members as any}
                                    />
                                  )}
                                </Stack>
                              </div>
                            </Fade>
                          )}

                          {group && group.isRuleBasedSelection() && (
                            <Fade in={true}>
                              <div>
                                <Stack direction='column' spacing={2} sx={{ padding: '16px 0' }}>
                                  <MultiSelector<SettingsJob>
                                    onChange={this.onJobsChange.bind(this)}
                                    hasAllItem={true}
                                    allItemLabel={this.props.translate('addEditGroup.allJobs')}
                                    values={
                                      jobs
                                        ? jobs.jobs.filter((j: SettingsJob) =>
                                            group.jobDefinitionIds.includes(
                                              j.jobDefinitionId as string
                                            )
                                          )
                                        : null
                                    }
                                    // values={jobs ? jobs.jobs.filter(j => jobSelected.includes(j.jobDefinitionId)) : null}
                                    allSelected={group.includeAllJobDefinitions}
                                    disabled={saving}
                                    translate={this.props.translate}
                                    renderSelector={(props: RenderSelectorProps<SettingsJob>) => (
                                      <JobSelector<SettingsJob>
                                        {...props}
                                        label={this.props.translate('addEditGroup.jobsRequired')}
                                        placeholder={this.props.translate(
                                          'addEditGroup.searchAndSelectJobs'
                                        )}
                                        items={jobsSearchList ? jobsSearchList.jobs : null}
                                        onInputChange={this.onSearchJobs.bind(this)}
                                        onBlur={this.onJobsBlur.bind(this)}
                                        getItemLabel={(job) => job.name || ''}
                                        compareItems={(a: SettingsJob, b: SettingsJob) =>
                                          a.jobDefinitionId === b.jobDefinitionId
                                        }
                                        callApi={false}
                                        multiple={false}
                                        clearable={true}
                                        loading={!groupLoaded || !jobs}
                                      />
                                    )}
                                  />

                                  <MultiSelector<Location>
                                    onChange={this.onLocationsChange.bind(this)}
                                    hasAllItem={true}
                                    allItemLabel={this.props.translate('addEditGroup.allLocations')}
                                    //values={group.locationIds}
                                    values={
                                      locations
                                        ? locations.filter((loc: Location) =>
                                            group.locationIds.includes(loc.locationId)
                                          )
                                        : null
                                    }
                                    allSelected={group.includeAllLocations}
                                    disabled={saving}
                                    translate={this.props.translate}
                                    renderSelector={(
                                      props: RenderSelectorProps<BaseSelectorOption & Location>
                                    ) => (
                                      <LocationSelector
                                        label={this.props.translate(
                                          'addEditGroup.locationsRequired'
                                        )}
                                        placeholder={this.props.translate(
                                          'addEditGroup.searchAndSelectLocations'
                                        )}
                                        items={locations ? locations : null}
                                        callApi={false}
                                        multiple={false}
                                        compareItems={(a, b) => a.locationId === b.locationId}
                                        getItemLabel={(location) => location.name}
                                        loading={!groupLoaded || !locations}
                                        {...props}
                                      />
                                    )}
                                  />

                                  {ruleBasedMembersCount &&
                                    this.isRuleValid(group) &&
                                    this.renderManagerMemberOverlapAlert()}

                                  {ruleBasedMembersCount && this.isRuleValid(group) && (
                                    <Alert severity='info' sx={{ width: '100%' }}>
                                      <Stack
                                        direction='row'
                                        justifyContent='space-between'
                                        sx={{ width: '100%' }}
                                      >
                                        <div>
                                          {updatingRuleBasedMembersCount
                                            ? this.props.translate('addEditGroup.updating')
                                            : Utils.pluralize(
                                                ruleBasedMembersCount.totalEmployees,
                                                'employee'
                                              ) +
                                              ' ' +
                                              Utils.isOrAre(ruleBasedMembersCount.totalEmployees) +
                                              ' ' +
                                              this.props.translate(
                                                'addEditGroup.matchingYourCriteria'
                                              )}
                                        </div>
                                        {!updatingRuleBasedMembersCount &&
                                          ruleBasedMembersCount.totalEmployees !== 0 && (
                                            <div>
                                              <Link
                                                onClick={this.onViewRuleBasedEmployees.bind(this)}
                                              >
                                                {this.props.translate('addEditGroup.viewEmployees')}
                                              </Link>
                                            </div>
                                          )}
                                      </Stack>
                                    </Alert>
                                  )}
                                </Stack>

                                {showRuleBasedEmployeesDialog && ruleBasedMembers && (
                                  <GroupMembersDialog
                                    theme={theme}
                                    employees={ruleBasedMembers.employees}
                                    onCancel={this.onCloseRuleBasedEmployeesDialog.bind(this)}
                                  />
                                )}
                              </div>
                            </Fade>
                          )}
                        </Stack>
                      </div>
                    </Stack>
                  </div>
                </Fade>
              )}

              {step === 1 && (
                <Fade in={true}>
                  <div>
                    <Stack direction='column' spacing={2}>
                      <div className='klayo-instruction klayo-instruction-dark'>
                        {this.props.translate('addEditGroup.selectEmployeesForAccess')}
                      </div>

                      <Stack spacing={4} direction='column'>
                        <div>
                          <EmployeeSelector
                            theme={theme}
                            hasNoneItem={false}
                            multiple={false}
                            label={null as any}
                            clearable={false}
                            clearOnSelection={true}
                            clearResultsOnSelection={true}
                            disabled={saving}
                            excludeItems={group ? (group.managers as any) : null}
                            placeholder={this.props.translate('addEditGroup.searchAndAddEmployees')}
                            onChange={this.onManagerChange.bind(this) as any}
                            initialItems={groupMembers ? (groupMembers as any) : null}
                            callApi={false}
                            translate={this.props.translate}
                          />
                        </div>

                        {group && group.managers && group.managers.length > 0 && (
                          <Stack spacing={2} direction='column'>
                            {this.renderManagerMemberOverlapAlert()}
                            <EmployeeTable
                              pagination={false}
                              showFooter={false}
                              rowHasAction={() => true}
                              theme={theme}
                              showHeaderFromBreakpoint='md'
                              translate={this.props.translate}
                              disabled={saving}
                              dense={false}
                              columns={[
                                { type: EmployeeTable.columns.profilePicFullNameJobTitle },
                                {
                                  type: EmployeeTable.columns.groupLevelType,
                                  groupLevelTypes: this.getTranslatedGroupLevelTypes(),
                                },
                                {
                                  type: EmployeeTable.columns.groupNotificationType,
                                  groupNotificationTypes:
                                    this.getTranslatedGroupNotificationTypes(),
                                },
                                { type: EmployeeTable.columns.actions },
                              ]}
                              sortModel={[
                                {
                                  field: 'profilePicFullName',
                                  sort: 'asc',
                                },
                              ]}
                              onGroupLevelTypeChange={
                                this.onManagerGroupLevelTypeChange.bind(this) as any
                              }
                              onGroupNotificationTypesChange={
                                this.onManagerGroupNotificationTypesChange.bind(this) as any
                              }
                              onEmployeeAction={this.onAccessEmployeeAction.bind(this) as any}
                              paper={false}
                              rows={group.managers as any}
                            />
                          </Stack>
                        )}

                        {!group ||
                          !group.managers ||
                          (group.managers.length === 0 && (
                            <Placeholder
                              text={this.props.translate('addEditGroup.useSelectorToAddEmployees')}
                              icon={<UserIcon />}
                              sx={{ margin: '64px 0 32px 0!important' }}
                            />
                          ))}
                      </Stack>
                    </Stack>
                  </div>
                </Fade>
              )}

              <Stack direction='row' justifyContent='space-between' alignItems='center' spacing={2}>
                <Button
                  onClick={this.onCancel.bind(this)}
                  disabled={saving}
                  label={this.props.translate('common.cancel')}
                />
                <Stack direction='row' spacing={2}>
                  {step > 0 && !editMode && (
                    <Button
                      variant='outlined'
                      onClick={this.onBack.bind(this)}
                      disabled={saving}
                      startIcon={<LeftIcon />}
                      label={this.props.translate('common.back')}
                    />
                  )}
                  <Button
                    variant='filled'
                    disabled={!this.isValid(group, step) || !hasEdit || saving || !groupLoaded}
                    onClick={this.onSave.bind(this)}
                    endIcon={!editMode && step === 0 ? <RightIcon /> : null}
                    label={
                      editMode
                        ? this.props.translate('common.save')
                        : step === 0
                          ? this.props.translate('common.continue')
                          : this.props.translate('common.create')
                    }
                  />
                </Stack>
              </Stack>

              {actionEmployee && (
                <Menu
                  open={actionTarget !== null}
                  anchorEl={actionTarget}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  onClose={this.onCloseActionMenu.bind(this)}
                >
                  <MenuItem onClick={(e) => this.onRemoveManager(e, actionEmployee)}>
                    <ListItemText>{this.props.translate('addEditGroup.removeMember')}</ListItemText>
                  </MenuItem>
                  <MenuItem onClick={() => this.onAssignAsInstructor(!actionEmployee.isInstructor)}>
                    <ListItemText>
                      {actionEmployee.isInstructor
                        ? this.props.translate('addEditGroup.unassignAsInstructor')
                        : this.props.translate('addEditGroup.assignAsInstructor')}
                    </ListItemText>
                  </MenuItem>
                  <MenuItem onClick={() => this.onOpenTrainingManagerPermissionsDialog()}>
                    <ListItemText>
                      {actionEmployee.isTrainingManager
                        ? this.props.translate('addEditGroup.editTrainingManagerPermissions')
                        : this.props.translate('addEditGroup.assignAsTrainingManager')}
                    </ListItemText>
                  </MenuItem>
                </Menu>
              )}
            </Stack>
          </PaperOrDialog>
        </EditTemplate>
        <TrainingManagerPermissionsDialog
          isOpen={this.state.isOpenTrainingManagerPermissionsDialog}
          onClose={() => this.setState({ isOpenTrainingManagerPermissionsDialog: false })}
          onAssign={this.onAssignTrainingManagerPermissions.bind(this)}
          trainingManagerConfigurationTypeId={
            this.state.actionEmployee?.trainingManagerConfigurationTypeId
          }
          isTrainingManager={this.state.actionEmployee?.isTrainingManager}
        />
      </>
    );
  }
}

export const AddEditGroupView = withLocale(AddEditGroupViewBase);
